// src/hooks/useAccountStatus.js
import { useState, useEffect } from 'react';
import { useAuth0 } from '@/hooks/useAuth';
import { syncAccount } from '@/services/apiService';
import { SKIP_AUTH, TENANT_ID } from '@/lib/config';

/**
 * Sincroniza a conta autenticada com o backend logo após o login e expõe
 * o status de aprovação (Pendente/Aprovado/Rejeitado) e se é admin.
 * Cobre os dois fluxos de login (senha e Google), já que ambos terminam
 * aqui assim que `isAuthenticated` fica true.
 *
 * A conta fica vinculada ao condomínio do primeiro acesso. Se ela pertence a
 * outro condomínio (e não tem acesso global), o status vira 'OutroCondominio'
 * — as APIs recusariam as chamadas de qualquer forma.
 */
export function useAccountStatus() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth0();
  const [status, setStatus] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (SKIP_AUTH) {
      setStatus('Aprovado');
      setIsAdmin(true);
      setIsLoading(false);
      return;
    }

    if (authLoading) return;

    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await syncAccount({
          email: user?.email || '',
          name: user?.name || '',
        });
        if (cancelled) return;
        const outroCondominio = !!result.tenantId && !result.acessoGlobal
          && result.tenantId.toLowerCase() !== String(TENANT_ID).toLowerCase();
        setStatus(outroCondominio ? 'OutroCondominio' : result.status);
        setIsAdmin(!outroCondominio && !!result.isAdmin);
      } catch (err) {
        if (cancelled) return;
        // Falha ao sincronizar: trata como pendente pra não liberar acesso indevido.
        setStatus('Pendente');
        setIsAdmin(false);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, user?.email, user?.name]);

  return { status, isAdmin, isLoading };
}
